import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

// Detects installability and shows a friendly "Add to Home Screen" prompt.
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Already installed / running standalone -> don't show
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    if (localStorage.getItem("uw_pwa_dismissed") === "1") return;

    const ua = window.navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua);
    setIsIOS(ios);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS has no beforeinstallprompt — show manual instructions after a delay
    let t;
    if (ios) {
      t = setTimeout(() => setVisible(true), 3500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {}
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("uw_pwa_dismissed", "1");
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-pwa-banner"
      className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-6 sm:w-[360px] z-[95] animate-[slideup_0.4s_ease]"
    >
      <div className="bg-[#16110E] text-[#F3EAD7] rounded-2xl shadow-2xl p-4 border border-[#241E19]">
        <div className="flex items-start gap-3">
          <img
            src={`${process.env.PUBLIC_URL || ""}/icons/icon-192.png`}
            alt="Uncle Willie's"
            className="w-12 h-12 rounded-xl shrink-0"
          />
          <div className="flex-1">
            <p className="font-display font-bold text-lg leading-tight">
              Get the Uncle Willie's app
            </p>
            {isIOS ? (
              <p className="text-sm text-[#D7CBB0] mt-1">
                Add it to your Home Screen — no App Store needed.
              </p>
            ) : (
              <p className="text-sm text-[#D7CBB0] mt-1">
                Install it on your phone — no app store needed.
              </p>
            )}
          </div>
          <button
            data-testid="install-pwa-dismiss"
            onClick={dismiss}
            className="text-[#7FB4C2] hover:text-[#F3EAD7] transition-colors shrink-0"
            aria-label="Dismiss"
          >
            <X size={20} />
          </button>
        </div>

        {isIOS ? (
          <ol className="mt-3 space-y-2 text-sm text-[#F3EAD7]">
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#F0C766] text-[#16110E] text-xs font-bold flex items-center justify-center shrink-0">
                1
              </span>
              <span className="flex items-center gap-1 flex-wrap">
                Tap the
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#241E19] text-[#7FB4C2]">
                  <Share size={14} /> Share
                </span>
                button below
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#F0C766] text-[#16110E] text-xs font-bold flex items-center justify-center shrink-0">
                2
              </span>
              <span className="flex items-center gap-1 flex-wrap">
                Choose
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#241E19] text-[#F3EAD7]">
                  <Plus size={14} /> Add to Home Screen
                </span>
              </span>
            </li>
          </ol>
        ) : (
          <button
            data-testid="install-pwa-button"
            onClick={install}
            className="mt-3 inline-flex items-center gap-2 bg-[#F0C766] text-[#16110E] px-4 py-2 rounded-full text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5"
          >
            <Download size={15} /> Install App
          </button>
        )}
      </div>
    </div>
  );
}
